import { GraphRequest } from '@microsoft/microsoft-graph-client';
import logger from 'services/Logging/logService';
import { sleep } from 'utils/timer';

/** ---------- Constants ---------- */
const MAX_ATTEMPTS = 5; // total attempts incl. the first one
const BASE_DELAY_MS = 500; // base backoff
const BACKOFF_FACTOR = 2; // exponential factor
const MAX_BACKOFF_MS = 20_000; // cap per wait
const JITTER = 0.2; // +/- 20% jitter

/** Exponential backoff with jitter */
const backoff = (attempt: number) => {
  const raw = Math.min(BASE_DELAY_MS * Math.pow(BACKOFF_FACTOR, attempt - 1), MAX_BACKOFF_MS);
  const j = raw * JITTER;

  return Math.floor(raw - j + Math.random() * (2 * j));
};

/** Parse common Graph/HTTP error fields safely */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseError = (err: any) => {
  const status =
    err?.statusCode ?? err?.status ?? err?.response?.status ?? (typeof err?.code === 'number' ? err.code : undefined);

  const code: string = err?.code || err?.body?.error?.code || err?.error?.code || err?.innerError?.code || '';

  const message: string = err?.message || err?.body?.error?.message || err?.error?.message || '';

  const requestId: string =
    err?.requestId || err?.innerError?.['request-id'] || err?.headers?.get?.('request-id') || '';

  return { status: Number(status) || undefined, code, message, requestId };
};

/**
 * Decide if we should retry (beyond what the SDK already does).
 * IMPORTANT: We explicitly DO NOT retry 429, 503, or 504 here — the SDK's RetryHandler handles those.
 * For non-idempotent methods (POST/PATCH by default), we keep retries extra conservative.
 */
const isRetryable = (method: string, idempotent: boolean, status?: number, code?: string, message?: string) => {
  // Explicitly exclude: handled by SDK RetryHandler
  if (status === 429 || status === 503 || status === 504) return false;

  // Transient SharePoint/Graph 500 generalException
  if (status === 500 && (code === 'generalException' || !code)) {
    return idempotent; // retry only if it's safe(ish)
  }

  // 502 often indicates edge/gateway flakiness
  if (status === 502) {
    // For non-idempotent methods, 502 could lead to duplicate side effects.
    return idempotent;
  }

  // Intermittent network errors (client side). We *may* retry POST/PATCH if caller opted-in.
  const msg = (message || '').toLowerCase();
  if (
    msg.includes('networkerror') ||
    msg.includes('ecconnreset') ||
    msg.includes('etimedout') ||
    msg.includes('failed to fetch') ||
    msg.includes('load failed')
  ) {
    return idempotent;
  }

  return false;
};

/** Core executor: runs the provided call and applies extra retries per our rules */
type ExecOptions = {
  maxAttempts?: number;
  idempotent?: boolean; // default chosen per method wrapper
};

async function executeWithRetry<T>(
  method: 'GET' | 'PUT' | 'POST' | 'PATCH' | 'DELETE',
  call: () => Promise<T>,
  options: ExecOptions,
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? MAX_ATTEMPTS;
  const idempotent = options.idempotent ?? (method === 'GET' || method === 'PUT' || method === 'DELETE');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let lastErr: any;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await call();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      lastErr = err;
      const { status, code, message, requestId } = parseError(err);

      if (!isRetryable(method, idempotent, status, code, message) || attempt >= maxAttempts) {
        break;
      }

      const wait = backoff(attempt);

      logger.debug(`[Graph:${method}] attempt ${attempt}/${maxAttempts} failed; retrying in ${wait}ms`, {
        status: status ?? 'n/a',
        code: code || 'n/a',
        requestId: requestId || 'n/a',
      });
      
      await sleep(wait);
    }
  }

  throw lastErr;
}

/** ----------------- Public wrappers ----------------- */

/**
 * GET – safe to retry by default (idempotent = true)
 */
export async function execGet(request: GraphRequest, options: ExecOptions = {}) {
  return executeWithRetry('GET', () => request.get(), options);
}

/**
 * PUT – generally idempotent; safe to retry by default (idempotent = true)
 * Usage: execPut(client.api("/me/drive/root:/file.txt:/content"), bufferOrStream)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function execPut<TBody = any>(request: GraphRequest, body: TBody, options: ExecOptions = {}) {
  return executeWithRetry('PUT', () => request.put(body), options);
}

/**
 * POST – NOT idempotent by default; retries OFF unless caller opts in via options.idempotent = true
 * Usage: execPost(client.api("/sites/{id}/lists"), { displayName: "X" }, { idempotent: true }) // only if you made it safe
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function execPost<TBody = any>(request: GraphRequest, body: TBody, options: ExecOptions = {}) {
  // default idempotent=false for POST
  return executeWithRetry('POST', () => request.post(body), { ...options, idempotent: options.idempotent ?? false });
}

/**
 * PATCH – NOT idempotent by default; retries OFF unless caller opts in
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export async function execPatch<TBody = any>(request: GraphRequest, body: TBody, options: ExecOptions = {}) {
  return executeWithRetry('PATCH', () => request.patch(body), { ...options, idempotent: options.idempotent ?? false });
}

/**
 * DELETE – usually idempotent in HTTP semantics; default idempotent = true
 * Note: a retried DELETE may return 404 on the second attempt if the first actually succeeded.
 */
export async function execDelete(request: GraphRequest, options: ExecOptions = {}) {
  return executeWithRetry('DELETE', () => request.delete(), options);
}
